import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-foreground text-background hover:bg-foreground/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs bg-secondary hover:bg-secondary-hover border",
        secondary: "bg-secondary text-muted-foreground hover:bg-secondary/80",
        "secondary-destructive":
          "bg-destructive/10 text-destructive hover:bg-destructive/15 dark:bg-destructive/20 dark:hover:bg-destructive/30",
        ghost:
          "hover:bg-muted bg-transparent text-muted-foreground hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        dashed:
          "border-dashed border-foreground hover:bg-foreground/10 border border-muted-foreground text-muted-foreground",
      },
      size: {
        default:
          "h-8 rounded-sm gap-1.5 px-3 has-[>svg,>canvas]:px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        xxs: "h-5 rounded-sm gap-1 px-1.5 has-[>svg,>canvas]:px-1 text-[9px] [&_svg:not([class*='size-'])]:size-3",
        xs: "h-7 rounded-sm gap-1 px-2 has-[>svg,>canvas]:px-1.5 text-[10px] [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg,>canvas]:px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 rounded-sm px-6 has-[>svg,>canvas]:px-4",
        icon: "size-8",
        "icon-xxs": "size-6 min-w-6 p-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-xs": "size-7",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/** Même taille que l'icône de tête de chaque variante de taille. */
const SPINNER_SIZE: Record<
  NonNullable<VariantProps<typeof buttonVariants>["size"]>,
  string
> = {
  default: "size-3.5",
  xxs: "size-3",
  xs: "size-3.5",
  sm: "size-3.5",
  lg: "size-4",
  icon: "size-4",
  "icon-xxs": "size-3",
  "icon-xs": "size-3.5",
  "icon-sm": "size-4",
  "icon-lg": "size-4",
};

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  children,
  disabled,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Attente en cours : le bouton se désactive et son icône devient un spinner. */
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  // `Slot` n'accepte qu'un enfant : on n'injecte le spinner que sur un vrai bouton.
  const showSpinner = loading && !asChild;

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant, size, className }),
        // Le spinner prend la place de l'icône de tête : la largeur ne bouge pas.
        showSpinner &&
          "[&>svg:not([data-slot=button-spinner])]:hidden [&>canvas]:hidden",
      )}
      {...props}
    >
      {/* Avec `asChild`, l'enfant passe seul : un emplacement `null` à côté
          compterait pour un second enfant et `Slot` lèverait `Children.only`. */}
      {asChild ? (
        children
      ) : (
        <>
          {showSpinner ? (
            <Spinner
              data-slot="button-spinner"
              className={SPINNER_SIZE[size ?? "default"]}
            />
          ) : null}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
